import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type DeviceKind = "audioinput" | "audiooutput";

interface DevicesStoreType {
  preferredMicrophoneId: string | null;
  preferredSpeakerId: string | null;
  currentMicrophoneId: string | null;
  currentSpeakerId: string | null;
  hasPermissions: boolean;
  isApplying: boolean;
  lastError: string | null;
  setPreferredDevice: (kind: DeviceKind, deviceId: string | null) => void;
  setCurrentDevice: (kind: DeviceKind, deviceId: string | null) => void;
  setHasPermissions: (hasPermissions: boolean) => void;
  setApplying: (value: boolean) => void;
  setLastError: (message: string | null) => void;
}

const noopStorageOperation = () => {
  // No-op fallback for environments without localStorage.
};

const createStorage = () =>
  createJSONStorage(() => {
    if (typeof window === "undefined") {
      return {
        clear: noopStorageOperation,
        getItem: () => null,
        key: () => null,
        length: 0,
        removeItem: noopStorageOperation,
        setItem: noopStorageOperation,
      } as Storage;
    }

    return window.localStorage;
  });

export const useDevicesStore = create<DevicesStoreType>()(
  persist(
    (set) => ({
      currentMicrophoneId: null,
      currentSpeakerId: null,
      hasPermissions: false,
      isApplying: false,
      lastError: null,
      preferredMicrophoneId: null,
      preferredSpeakerId: null,
      setApplying: (isApplying) => set({ isApplying }),
      setCurrentDevice: (kind, deviceId) =>
        set(
          kind === "audioinput"
            ? { currentMicrophoneId: deviceId }
            : { currentSpeakerId: deviceId }
        ),
      setHasPermissions: (hasPermissions) => set({ hasPermissions }),
      setLastError: (lastError) => set({ lastError }),
      setPreferredDevice: (kind, deviceId) =>
        set(
          kind === "audioinput"
            ? { preferredMicrophoneId: deviceId }
            : { preferredSpeakerId: deviceId }
        ),
    }),
    {
      name: "table-call-audio-devices",
      partialize: (state) => ({
        preferredMicrophoneId: state.preferredMicrophoneId,
        preferredSpeakerId: state.preferredSpeakerId,
      }),
      storage: createStorage(),
    }
  )
);

export const selectPreferredMicrophoneId = (state: DevicesStoreType) =>
  state.preferredMicrophoneId;
export const selectPreferredSpeakerId = (state: DevicesStoreType) =>
  state.preferredSpeakerId;
export const selectCurrentMicrophoneId = (state: DevicesStoreType) =>
  state.currentMicrophoneId;
export const selectCurrentSpeakerId = (state: DevicesStoreType) =>
  state.currentSpeakerId;
export const selectDevicesApplying = (state: DevicesStoreType) =>
  state.isApplying;
export const selectDevicesError = (state: DevicesStoreType) => state.lastError;
